import { onUnmounted, ref, watch, type Ref } from 'vue';
import type { InspectionDto } from '@carvia/ros-client-types';
import { useEvidenceUploadQueue } from '@/hooks/use-evidence-upload-queue';

/**
 * Object URLs for Inspection shots whose photo is still queued on this device, so the agent sees
 * the capture before it lands. Only the current capture of each shot is shown.
 */
export function useInspectionLocalPreviews(inspection: Ref<InspectionDto | undefined>) {
  const { items } = useEvidenceUploadQueue();
  const previews = ref<Record<string, string>>({});
  const urlsByItemId = new Map<string, string>();

  watch(
    [items, inspection],
    () => {
      const current = inspection.value;
      const next: Record<string, string> = {};
      const liveItemIds = new Set<string>();
      for (const item of items.value) {
        if (!current || item.kind !== 'inspection-shot' || item.inspectionId !== current.id) {
          continue;
        }
        const shot = current.shots.find((row) => row.shotKey === item.shotKey);
        if (!shot || shot.captureId !== item.captureId) continue;
        let url = urlsByItemId.get(item.id);
        if (!url) {
          url = URL.createObjectURL(item.blob);
          urlsByItemId.set(item.id, url);
        }
        liveItemIds.add(item.id);
        next[item.shotKey!] = url;
      }
      for (const [itemId, url] of urlsByItemId) {
        if (liveItemIds.has(itemId)) continue;
        URL.revokeObjectURL(url);
        urlsByItemId.delete(itemId);
      }
      previews.value = next;
    },
    { immediate: true, deep: true },
  );

  onUnmounted(() => {
    for (const url of urlsByItemId.values()) URL.revokeObjectURL(url);
    urlsByItemId.clear();
  });

  return previews;
}
